import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Tabs,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Upload,
  message,
  Table,
  Space,
  Popconfirm,
  Switch,
  Image,
  Empty,
  Spin,
  Badge,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  CameraOutlined,
  SaveOutlined,
  ReloadOutlined,
  MenuOutlined,
  ShoppingCartOutlined,
  PictureOutlined
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload';
import axios from 'axios';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

interface Category {
  id: number;
  name: string;
  slug: string;
  display_order: number;
  is_active: boolean;
}

interface Product {
  id: number;
  category_id: number;
  category_name: string;
  name: string;
  price: number;
  description: string;
  image_url: string;
  thumbnail_url: string;
  display_order: number;
  is_available: boolean;
  is_active: boolean;
}

const BentoMenuManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const [categoryForm] = Form.useForm();
  const [productForm] = Form.useForm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  // データ取得
  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/bento-menu/categories`);
      setCategories(response.data);
    } catch (error) {
      message.error('カテゴリの取得に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/bento-menu/products`);
      setProducts(response.data);
    } catch (error) {
      message.error('商品の取得に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // カテゴリ管理
  const handleCategorySubmit = async (values: any) => {
    setLoading(true);
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/bento-menu/categories/${editingCategory.id}`, values);
        message.success('カテゴリを更新しました');
      } else {
        await axios.post(`${API_URL}/bento-menu/categories`, values);
        message.success('カテゴリを作成しました');
      }
      setCategoryModalVisible(false);
      categoryForm.resetFields();
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      message.error('操作に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryDelete = async (id: number) => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/bento-menu/categories/${id}`);
      message.success('カテゴリを削除しました');
      fetchCategories();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'カテゴリの削除に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 商品管理
  const handleProductSubmit = async (values: any) => {
    console.log('🔧 handleProductSubmit called with values:', values);
    console.log('🔧 editingProduct:', editingProduct);
    console.log('🔧 fileList:', fileList);
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        if (values[key] !== undefined && values[key] !== null) {
          // ブール値を整数に変換（SQLite互換性のため）
          let value = values[key];
          if (typeof value === 'boolean') {
            value = value ? 1 : 0;
          }
          formData.append(key, value);
        }
      });

      // 画像ファイルを追加
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('image', fileList[0].originFileObj);
      }

      if (editingProduct) {
        await axios.put(`${API_URL}/bento-menu/products/${editingProduct.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('商品を更新しました');
      } else {
        await axios.post(`${API_URL}/bento-menu/products`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('商品を作成しました');
      }

      setProductModalVisible(false);
      productForm.resetFields();
      setEditingProduct(null);
      setFileList([]);
      fetchProducts();
    } catch (error: any) {
      console.error('❌ Error in handleProductSubmit:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      const errorMsg = error.response?.data?.error || error.message || '操作に失敗しました';
      message.error(`エラー: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProductDelete = async (id: number) => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/bento-menu/products/${id}`);
      message.success('商品を削除しました');
      fetchProducts();
    } catch (error) {
      message.error('商品の削除に失敗しました');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 画像アップロード処理
  const handleImageChange = (info: any) => {
    setFileList(info.fileList);
  };

  const uploadProps = {
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('画像ファイルのみアップロード可能です');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('画像は5MB以下にしてください');
        return false;
      }
      return false; // 自動アップロードを防ぐ
    },
    fileList,
    onChange: handleImageChange,
    maxCount: 1
  };

  // スマホカメラ対応
  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileList([{
        uid: '-1',
        name: file.name,
        status: 'done',
        originFileObj: file as any,
        thumbUrl: URL.createObjectURL(file)
      }]);
    }
  };

  // 表示順変更（ボタンで上下移動）
  const handleMoveProduct = async (product: Product, direction: 'up' | 'down') => {
    const categoryProducts = products.filter(p => p.category_id === product.category_id);
    const currentIndex = categoryProducts.findIndex(p => p.id === product.id);

    if ((direction === 'up' && currentIndex === 0) ||
        (direction === 'down' && currentIndex === categoryProducts.length - 1)) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const items = [...categoryProducts];
    const [moved] = items.splice(currentIndex, 1);
    items.splice(newIndex, 0, moved);

    // 表示順を更新
    const updates = items.map((item, index) => ({
      id: item.id,
      display_order: index + 1
    }));

    try {
      await axios.post(`${API_URL}/bento-menu/products/reorder`, { items: updates });
      fetchProducts();
      message.success('表示順を更新しました');
    } catch (error) {
      message.error('表示順の更新に失敗しました');
      console.error(error);
    }
  };

  // フィルタリングされた商品
  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category_id === parseInt(selectedCategory));

  // カテゴリテーブルのカラム
  const categoryColumns = [
    {
      title: '表示順',
      dataIndex: 'display_order',
      key: 'display_order',
      width: 80,
      sorter: (a: Category, b: Category) => a.display_order - b.display_order
    },
    {
      title: 'カテゴリ名',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'スラッグ',
      dataIndex: 'slug',
      key: 'slug'
    },
    {
      title: '状態',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '有効' : '無効'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Category) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingCategory(record);
              categoryForm.setFieldsValue(record);
              setCategoryModalVisible(true);
            }}
          />
          <Popconfirm
            title="カテゴリを削除しますか？"
            description="このカテゴリに属する商品がある場合は削除できません"
            onConfirm={() => handleCategoryDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 商品カード（レスポンシブ対応）
  const ProductCard: React.FC<{ product: Product }> = ({ product }) => (
    <Card
      hoverable
      style={{ marginBottom: 16 }}
      cover={
        product.thumbnail_url ? (
          <Image
            alt={product.name}
            src={product.thumbnail_url}
            height={200}
            style={{ objectFit: 'cover' }}
            preview={{
              src: product.image_url
            }}
          />
        ) : (
          <div style={{ height: 200, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PictureOutlined style={{ fontSize: 48, color: '#999' }} />
          </div>
        )
      }
      actions={[
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            setEditingProduct(product);
            productForm.setFieldsValue(product);
            setProductModalVisible(true);
          }}
        >
          編集
        </Button>,
        <Popconfirm
          title="商品を削除しますか？"
          onConfirm={() => handleProductDelete(product.id)}
        >
          <Button icon={<DeleteOutlined />} danger>削除</Button>
        </Popconfirm>
      ]}
    >
      <Card.Meta
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{product.name}</span>
            <Badge
              count={product.is_available ? '販売中' : '売切'}
              style={{
                backgroundColor: product.is_available ? '#52c41a' : '#ff4d4f'
              }}
            />
          </div>
        }
        description={
          <>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa541c', margin: '8px 0' }}>
              ¥{product.price.toLocaleString()}
            </div>
            <div style={{ color: '#666' }}>{product.description}</div>
            <div style={{ marginTop: 8 }}>
              <Tag>{product.category_name}</Tag>
            </div>
          </>
        }
      />
    </Card>
  );

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShoppingCartOutlined />
          <span>弁当メニュー管理</span>
        </div>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            fetchCategories();
            fetchProducts();
          }}
        >
          更新
        </Button>
      }
    >
      <Tabs defaultActiveKey="products">
        <TabPane
          tab={
            <span>
              <MenuOutlined />
              商品管理
            </span>
          }
          key="products"
        >
          <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Select
              style={{ width: 200 }}
              placeholder="カテゴリで絞込"
              value={selectedCategory}
              onChange={setSelectedCategory}
            >
              <Option value="all">すべて</Option>
              {categories.map(cat => (
                <Option key={cat.id} value={cat.id.toString()}>
                  {cat.name}
                </Option>
              ))}
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingProduct(null);
                productForm.resetFields();
                setFileList([]);
                setProductModalVisible(true);
              }}
            >
              新規商品追加
            </Button>
          </div>

          <Spin spinning={loading}>
            {filteredProducts.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <Empty description="商品がありません" />
            )}
          </Spin>
        </TabPane>

        <TabPane
          tab={
            <span>
              <MenuOutlined />
              カテゴリ管理
            </span>
          }
          key="categories"
        >
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCategory(null);
                categoryForm.resetFields();
                setCategoryModalVisible(true);
              }}
            >
              新規カテゴリ追加
            </Button>
          </div>

          <Table
            columns={categoryColumns}
            dataSource={categories}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        </TabPane>
      </Tabs>

      {/* カテゴリ編集モーダル */}
      <Modal
        title={editingCategory ? 'カテゴリ編集' : 'カテゴリ追加'}
        visible={categoryModalVisible}
        onCancel={() => {
          setCategoryModalVisible(false);
          setEditingCategory(null);
          categoryForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCategorySubmit}
        >
          <Form.Item
            name="name"
            label="カテゴリ名"
            rules={[{ required: true, message: 'カテゴリ名を入力してください' }]}
          >
            <Input placeholder="例: お弁当" />
          </Form.Item>

          <Form.Item
            name="slug"
            label="スラッグ（URLで使用）"
            rules={[{ required: true, message: 'スラッグを入力してください' }]}
          >
            <Input placeholder="例: bento" />
          </Form.Item>

          <Form.Item
            name="display_order"
            label="表示順"
            rules={[{ required: true, message: '表示順を入力してください' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          {editingCategory && (
            <Form.Item
              name="is_active"
              label="状態"
              valuePropName="checked"
            >
              <Switch checkedChildren="有効" unCheckedChildren="無効" />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingCategory ? '更新' : '作成'}
              </Button>
              <Button onClick={() => setCategoryModalVisible(false)}>
                キャンセル
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 商品編集モーダル */}
      <Modal
        title={editingProduct ? '商品編集' : '商品追加'}
        visible={productModalVisible}
        onCancel={() => {
          setProductModalVisible(false);
          setEditingProduct(null);
          productForm.resetFields();
          setFileList([]);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={productForm}
          layout="vertical"
          onFinish={handleProductSubmit}
          onFinishFailed={(errorInfo) => {
            console.error('🔴 Form validation failed:', errorInfo);
            message.error('入力内容を確認してください');
          }}
        >
          <Form.Item
            name="category_id"
            label="カテゴリ"
            rules={[{ required: true, message: 'カテゴリを選択してください' }]}
          >
            <Select placeholder="カテゴリを選択">
              {categories.map(cat => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="商品名"
            rules={[{ required: true, message: '商品名を入力してください' }]}
          >
            <Input placeholder="例: ロースかつ弁当" />
          </Form.Item>

          <Form.Item
            name="price"
            label="価格（税込）"
            rules={[{ required: true, message: '価格を入力してください' }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/\¥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="説明文"
          >
            <TextArea rows={3} placeholder="例: 柔らかロース肉・ご飯・副菜付き" />
          </Form.Item>

          <Form.Item label="商品画像">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Upload {...uploadProps} listType="picture-card">
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>画像選択</div>
                </div>
              </Upload>
              <Button
                icon={<CameraOutlined />}
                onClick={handleCameraCapture}
                style={{ height: 102 }}
              >
                カメラで撮影
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="camera"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
          </Form.Item>

          <Form.Item
            name="display_order"
            label="表示順"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="is_available"
            label="在庫状態"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="販売中" unCheckedChildren="売切" />
          </Form.Item>

          {editingProduct && (
            <Form.Item
              name="is_active"
              label="商品状態"
              valuePropName="checked"
            >
              <Switch checkedChildren="有効" unCheckedChildren="無効" />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                {editingProduct ? '更新' : '作成'}
              </Button>
              <Button onClick={() => setProductModalVisible(false)}>
                キャンセル
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default BentoMenuManager;